import { create } from 'zustand';
import type { PviGraphWithSpans } from '../editor/pviTypes';
import type { NodeId } from '../editor/pviTypes';
import type {
  ScenarioCategory,
  AdversarialResult,
  AdversarialRun,
} from '../editor/adversarialEngine';
import { runAdversarialAnalysis, runSingleScenario } from '../editor/adversarialEngine';

export type AdvMode = 'off' | 'adversarial';

interface AdversarialState {
  /** Whether adversarial mode is active. */
  mode: AdvMode;
  /** Full analysis result. */
  result: AdversarialResult | null;
  /** Currently running analysis. */
  running: boolean;
  /** Selected scenario category to view. */
  selectedScenario: ScenarioCategory | null;
  /** Selected run index within the selected scenario. */
  selectedRunId: number | null;
  /** Node IDs that failed in adversarial analysis (for graph overlay). */
  advFailedNodeIds: Set<NodeId>;
  /** Node IDs that had warnings. */
  advWarningNodeIds: Set<NodeId>;
  /** Node IDs that passed all tests. */
  advPassedNodeIds: Set<NodeId>;
  /** Max runs per scenario. */
  maxRunsPerScenario: number;

  // Actions
  setMode: (mode: AdvMode) => void;
  runAll: (graph: PviGraphWithSpans) => void;
  runCategory: (graph: PviGraphWithSpans, category: ScenarioCategory) => void;
  selectScenario: (category: ScenarioCategory | null) => void;
  selectRun: (runId: number | null) => void;
  reset: () => void;
  setMaxRuns: (max: number) => void;
}

/** Cache the current graph reference. */
let _advGraph: PviGraphWithSpans | null = null;
void _advGraph; // suppress TS6133

export const useAdversarialStore = create<AdversarialState>((set, get) => ({
  mode: 'off',
  result: null,
  running: false,
  selectedScenario: null,
  selectedRunId: null,
  advFailedNodeIds: new Set(),
  advWarningNodeIds: new Set(),
  advPassedNodeIds: new Set(),
  maxRunsPerScenario: 20,

  setMode: (mode) => {
    if (mode === 'off') {
      set({
        mode,
        result: null,
        running: false,
        selectedScenario: null,
        selectedRunId: null,
        advFailedNodeIds: new Set(),
        advWarningNodeIds: new Set(),
        advPassedNodeIds: new Set(),
      });
    } else {
      set({ mode });
    }
  },

  runAll: (graph) => {
    _advGraph = graph;
    const { maxRunsPerScenario } = get();
    set({ running: true });

    // Use setTimeout to avoid blocking the UI
    setTimeout(() => {
      const result = runAdversarialAnalysis(graph, undefined, maxRunsPerScenario);
      set({
        result,
        running: false,
        advFailedNodeIds: result.failedNodeIds,
        advWarningNodeIds: result.warningNodeIds,
        advPassedNodeIds: result.passedNodeIds,
      });
    }, 0);
  },

  runCategory: (graph, category) => {
    _advGraph = graph;
    const { result, maxRunsPerScenario } = get();
    set({ running: true });

    setTimeout(() => {
      const scenarioResult = runSingleScenario(graph, category, maxRunsPerScenario);

      // Merge into existing result or create new
      if (result) {
        const existing = result.scenarios.filter(s => s.category !== category);
        const allScenarios = [...existing, scenarioResult];

        const failedNodeIds = new Set<NodeId>();
        const warningNodeIds = new Set<NodeId>();
        let totalRuns = 0;
        let totalFailures = 0;
        let totalWarnings = 0;

        for (const sc of allScenarios) {
          totalRuns += sc.totalRuns;
          totalFailures += sc.totalFailures;
          totalWarnings += sc.totalWarnings;
          for (const run of sc.runs) {
            for (const f of run.failures) {
              if (f.nodeId !== null) failedNodeIds.add(f.nodeId);
            }
            for (const w of run.result.warnings) {
              const match = w.match(/Node #(\d+)/);
              if (match) warningNodeIds.add(parseInt(match[1], 10));
            }
          }
        }

        const allNodeIds = new Set<NodeId>(graph.nodes.map(n => n.id));
        const passedNodeIds = new Set<NodeId>();
        for (const nid of allNodeIds) {
          if (!failedNodeIds.has(nid) && !warningNodeIds.has(nid)) passedNodeIds.add(nid);
        }

        set({
          result: {
            ...result,
            scenarios: allScenarios,
            totalRuns,
            totalFailures,
            totalWarnings,
            failedNodeIds,
            warningNodeIds,
            passedNodeIds,
          },
          running: false,
          advFailedNodeIds: failedNodeIds,
          advWarningNodeIds: warningNodeIds,
          advPassedNodeIds: passedNodeIds,
        });
      } else {
        const allNodeIds = new Set<NodeId>(graph.nodes.map(n => n.id));
        const passedNodeIds = new Set<NodeId>();
        const failedNodeIds = new Set<NodeId>();
        const warningNodeIds = new Set<NodeId>();

        for (const run of scenarioResult.runs) {
          for (const f of run.failures) {
            if (f.nodeId !== null) failedNodeIds.add(f.nodeId);
          }
        }
        for (const nid of allNodeIds) {
          if (!failedNodeIds.has(nid)) passedNodeIds.add(nid);
        }

        set({
          result: {
            scenarios: [scenarioResult],
            totalRuns: scenarioResult.totalRuns,
            totalFailures: scenarioResult.totalFailures,
            totalWarnings: scenarioResult.totalWarnings,
            failedNodeIds,
            warningNodeIds,
            passedNodeIds,
            timestamp: Date.now(),
          },
          running: false,
          advFailedNodeIds: failedNodeIds,
          advWarningNodeIds: warningNodeIds,
          advPassedNodeIds: passedNodeIds,
        });
      }
    }, 0);
  },

  selectScenario: (category) => {
    set({ selectedScenario: category, selectedRunId: null });
  },

  selectRun: (runId) => {
    set({ selectedRunId: runId });
  },

  reset: () => {
    set({
      result: null,
      running: false,
      selectedScenario: null,
      selectedRunId: null,
      advFailedNodeIds: new Set(),
      advWarningNodeIds: new Set(),
      advPassedNodeIds: new Set(),
    });
  },

  setMaxRuns: (max) => {
    set({ maxRunsPerScenario: Math.max(1, Math.min(100, max)) });
  },
}));

/** Get the selected run from the current state. */
export function getSelectedRun(state: AdversarialState): AdversarialRun | null {
  if (!state.result || state.selectedScenario === null || state.selectedRunId === null) return null;
  const scenario = state.result.scenarios.find(s => s.category === state.selectedScenario);
  if (!scenario) return null;
  return scenario.runs.find(r => r.id === state.selectedRunId) ?? null;
}
